import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { RadioOrSelectOption, RootState } from '../../../types/common.types';
import { HashTag, TribeTemplate } from '../../../types/entity.types';

import { useApprovalApi } from '../../../api/approval/useApprovalApi';
import { useMemberApi } from '../../../api/member/useMemberApi';
import { usePostApi } from '../../../api/post/usePostApi';
import { useProgrammeApi } from '../../../api/programme/useProgrammeApi';
import { useReactionApi } from '../../../api/reaction/useReactionApi';
import { useTribeTemplateApi } from '../../../api/tribe-template/useTribeTemplateApi';

import {
  setPost,
  setPostHierarchy,
  setPostSearchResults,
  setPosts,
  setProfile
} from '../../../slices/programme/tribe/tribe.slice';

import { TRIBE_PAGE_SIZE, UPCOMING } from '../../../utils/constants';
import { generateUUID } from '../../../utils/util';

import Tribe from '../../../components/screens/programme/tribe/Tribe';

const TribeContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [hashtags, setHashtags] = useState<HashTag[]>([]);
  const [searchType, setSearchType] = useState<RadioOrSelectOption>({ label: 'Newest Posts', value: 'newest-posts' });

  const { tribeTemplate, posts, memberPosts, approvalCount, postHierarchy, postSearchResults, profile } = useSelector(
    ({ tribe }: RootState) => tribe
  );
  const { members } = useSelector(({ member }: RootState) => member);
  const user = useSelector(({ system }: RootState) => system.user);

  const { status, programmeExternalReference, memberExternalReference } = useParams();

  const { viewProgramme } = useProgrammeApi();

  const { viewTribeMembers, viewMemberProfile } = useMemberApi();

  const {
    viewTribeTemplate,
    createTribeTemplate: createTribeTemplateRequest,
    updateTribeTemplate: updateTribeTemplateRequest
  } = useTribeTemplateApi();

  const { searchPosts, viewPosts, viewPostHierarchy } = usePostApi();

  const { viewReactions } = useReactionApi();

  const { viewApprovalCount } = useApprovalApi();

  useEffect(() => {
    if (!programmeExternalReference) return;

    viewProgramme({ externalReference: programmeExternalReference });
    viewTribeTemplate({ programmeExternalReference });

    if (status !== UPCOMING) {
      viewTribeMembers({ programmeExternalReference });
      viewApprovalCount({ programmeExternalReference });
    }
  }, [status, programmeExternalReference, viewProgramme, viewTribeMembers, viewTribeTemplate, viewApprovalCount]);

  useEffect(() => {
    if (status !== UPCOMING) {
      viewPosts({
        page: 0,
        size: TRIBE_PAGE_SIZE,
        searchType: searchType.value,
        programmeExternalReference: programmeExternalReference!,
        memberExternalReference
      });
    }

    return () => {
      dispatch(setPosts([]));
    };
  }, [dispatch, viewPosts, programmeExternalReference, status, searchType, memberExternalReference]);

  useEffect(() => {
    if (members.length > 0 && user && status !== UPCOMING) {
      const member = members.find((member) => member.username === user.username);
      if (!member?.memberExternalReference) return;

      viewReactions({ memberExternalReference: member.memberExternalReference });
    }
  }, [viewReactions, status, members, user]);

  useEffect(() => {
    if (tribeTemplate?.hashTags) {
      const mappedHashtags = tribeTemplate.hashTags.map((hashtag) => ({
        id: generateUUID(),
        name: hashtag.text
      }));

      setHashtags(mappedHashtags);
    }
  }, [tribeTemplate]);

  useEffect(() => {
    if (!memberExternalReference) {
      dispatch(setProfile(undefined));
      return;
    }

    viewMemberProfile({ memberExternalReference });
  }, [dispatch, memberExternalReference, viewMemberProfile]);

  const createHashtag = () => {
    const hashtag = { id: generateUUID(), name: '', count: 0 };
    setHashtags((prevState) => [...prevState, hashtag]);
  };

  const updateHashtag = (id: string | number, value: string) => {
    const copyOfHashtags = structuredClone(hashtags);
    copyOfHashtags.forEach((hashtag) => {
      if (hashtag.id === id) {
        hashtag.name = value;
      }
    });

    setHashtags(copyOfHashtags);
  };

  const deleteHashtag = (id: string | number) => {
    setHashtags(hashtags.filter((hashtag) => hashtag.id !== id));
  };

  const createTribeTemplate = (tribeTemplate: TribeTemplate, redirectUrl?: string) => {
    createTribeTemplateRequest({ tribeTemplate, programmeExternalReference: programmeExternalReference! }, () => {
      if (!redirectUrl) return;
      navigate(redirectUrl);
    });
  };

  const updateTribeTemplate = (tribeTemplate: TribeTemplate, redirectUrl?: string) => {
    updateTribeTemplateRequest({ tribeTemplate, programmeExternalReference: programmeExternalReference! }, () => {
      if (!redirectUrl) return;
      navigate(redirectUrl);
    });
  };

  const loadPosts = () => {
    let page;
    if (memberExternalReference) {
      page = Math.floor(memberPosts.length / TRIBE_PAGE_SIZE);
    } else {
      page = Math.floor(posts.length / TRIBE_PAGE_SIZE);
    }

    viewPosts({
      programmeExternalReference: programmeExternalReference!,
      size: TRIBE_PAGE_SIZE,
      searchType: searchType.value,
      ammend: true,
      page,
      memberExternalReference
    });
  };

  const refreshPost = (postExternalReference: string) => {
    const postsToSearch = memberExternalReference ? memberPosts : posts;

    const indexOfPost = postsToSearch.findIndex((post) => post.externalReference === postExternalReference);

    viewPosts({
      programmeExternalReference: programmeExternalReference!,
      page: indexOfPost,
      size: 1,
      searchType: searchType.value,
      replace: true,
      memberExternalReference
    });

    const foundMember = members.find((member) => member.username === user?.username);
    viewReactions({ memberExternalReference: foundMember!.memberExternalReference! });
  };

  const clearPost = () => {
    dispatch(setPost(undefined));
    dispatch(setPostHierarchy(undefined));
  };

  const executeSearch = (searchValue: string) => {
    clearPost();
    searchPosts({
      programmeExternalReference: programmeExternalReference!,
      memberExternalReference,
      searchText: searchValue
    });
  };

  const handleLoadPostWithHierarchy = (externalReference: string) => {
    clearPostSearchResults();
    viewPostHierarchy({ postExternalReference: externalReference });
  };

  const clearPostSearchResults = () => {
    dispatch(setPostSearchResults(undefined));
  };

  return (
    <Tribe
      status={status as string}
      tribeTemplate={tribeTemplate}
      pendingApprovals={approvalCount}
      posts={posts}
      memberPosts={memberPosts}
      members={members}
      hashtags={hashtags}
      profilePicture={user?.profilePicture as string | undefined}
      searchType={searchType}
      createHashtag={createHashtag}
      updateHashtag={updateHashtag}
      deleteHashtag={deleteHashtag}
      createTribeTemplate={createTribeTemplate}
      updateTribeTemplate={updateTribeTemplate}
      loadPosts={loadPosts}
      refreshPost={refreshPost}
      clearPost={clearPost}
      setSearchType={setSearchType}
      searchResults={postSearchResults}
      clearPostSearchResults={clearPostSearchResults}
      executeSearch={executeSearch}
      loadPostWithHierarchy={handleLoadPostWithHierarchy}
      postWithHierarchy={postHierarchy}
      profile={profile}
    />
  );
};

export default TribeContainer;
